import React, { useContext, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Context } from "@components/useContext/context";
import AddonVariantsModal from "@components/addonVariantModals/addonVariantsModal";
import { executeActions, getWidgetSpecificProps } from "@utils/index";
import { formatToINR } from "@utils/inrFormatter";
import TpPaCoverModal, { TpPaModalCopy } from "./TpPaCoverModal";
import TpOnlyRenewalsUI, {
  BenefitCard,
  FooterConfig,
  PlanCard
} from "./TpOnlyRenewalsUI";
import {
  AddonSelectionMap,
  AddonVariant,
  PA_OWNER_ADDON_ID,
  ProposalAddonSelection,
  RSA_ADDON_ID,
  TP_PLAN_ID,
  TpAddonModel,
  TpPlanId,
  PlansPayload,
  areSelectionsValid,
  buildInitialSelections,
  buildTpEditModel,
  calculateTotalNetPremium,
  carrySelectionsToPlan,
  getDisplayAddonPremium,
  getSelectableAddons,
  isTpProPlan,
  selectionFromAddon,
  serializeSelections
} from "./model";

type ActionConfig = Record<string, Record<string, unknown>[]>;

type EditCopy = {
  title?: string;
  planSectionTitle?: string;
  benefitsSectionTitle?: string;
  planPriceNudgeTemplate?: string;
  addonTitleTemplate?: string;
  variantModal?: {
    headingTemplate?: string;
    optionsLabelTemplate?: string;
    primaryCtaLabel?: string;
    secondaryCtaLabel?: string;
  };
  paModal?: TpPaModalCopy;
  footer?: Pick<
    FooterConfig,
    "desktopEyebrow" | "desktopCtaLabel" | "mobileTaxLabel" | "mobileCtaLabel"
  >;
};

type TpOnlyRenewalsProps = {
  id: string;
  is_visible?: boolean;
  plans?: PlansPayload;
  selectedPlanId?: string;
  selectedAddons?: ProposalAddonSelection[];
  copy?: EditCopy;
  footer?: FooterConfig;
  actions?: ActionConfig;
};

type ReduxState = {
  configuration?: {
    normalisedConfig?: unknown;
  };
};

const mapStateToProps = (state: ReduxState, ownProps: { id: string }) =>
  getWidgetSpecificProps(state.configuration?.normalisedConfig, ownProps.id);

const formatPremium = (amount: number): string =>
  formatToINR(amount, true) || "₹0";

const warnForMissingCopy = (message: string) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[tp_only_renewals] ${message}`);
  }
};

const interpolateTemplate = (
  template: string | undefined,
  values: Record<string, string>
) => {
  if (!template?.trim()) return "";

  let hasMissingToken = false;
  const interpolated = template.replace(
    /\{\{\s*([a-z_]+)\s*\}\}/gi,
    (_match, token: string) => {
      const value = values[token];
      if (!value) {
        hasMissingToken = true;
        return "";
      }
      return value;
    }
  );

  return hasMissingToken || /\{\{.*\}\}/.test(interpolated)
    ? ""
    : interpolated.trim();
};

const getVariant = (
  addon: TpAddonModel,
  selection?: ProposalAddonSelection
): AddonVariant | undefined =>
  addon.variants.find(
    (variant) => variant.variant_id === selection?.variant_id
  );

const getPlanCards = (
  modelPlans: ReturnType<typeof buildTpEditModel>["plans"],
  activePlanId: TpPlanId,
  priceNudgeTemplate?: string
): PlanCard[] => {
  const tpPlan = modelPlans.find((plan) => plan.id === TP_PLAN_ID);

  return modelPlans.map((plan) => {
    const premiumDelta = tpPlan
      ? plan.baseNetPremium - tpPlan.baseNetPremium
      : 0;

    const badge =
      typeof plan.secondaryTag?.text === "string"
        ? plan.secondaryTag.text.trim()
        : "";
    if (!badge) {
      warnForMissingCopy(`${plan.id} is missing secondary_tag.text`);
    }
    const premiumDeltaLabel =
      isTpProPlan(plan.id) && premiumDelta > 0
        ? interpolateTemplate(priceNudgeTemplate, {
            premium_delta: formatPremium(premiumDelta)
          })
        : "";

    return {
      id: plan.id,
      badge: badge || undefined,
      badgeVariant:
        plan.secondaryTag?.type?.trim().toLowerCase() === "upgrade"
          ? "upgrade"
          : "neutral",
      title: plan.displayName,
      price: formatPremium(plan.baseNetPremium),
      priceNudge: premiumDeltaLabel || undefined,
      selected: plan.id === activePlanId,
      features: plan.features
    };
  });
};

const getBenefitCards = (
  plan: ReturnType<typeof buildTpEditModel>["plans"][number],
  selections: AddonSelectionMap,
  addonTitleTemplate?: string
): BenefitCard[] =>
  getSelectableAddons(plan).map((addon) => {
    const selection = selections[addon.id];
    const variant = getVariant(addon, selection);
    const premium = getDisplayAddonPremium(addon, selection);

    const title = interpolateTemplate(addonTitleTemplate, {
      display_name: addon.displayName,
      premium: formatPremium(premium)
    });
    if (!title) {
      warnForMissingCopy(`addon title could not be resolved for ${addon.id}`);
    }

    return {
      id: addon.id,
      tag: addon.badge?.text,
      title,
      description:
        variant?.detailed_description ||
        variant?.description ||
        addon.description,
      selected: Boolean(selection),
      disabled: !title,
      hasVariants: addon.variants.length > 1
    };
  });

const TpOnlyRenewals = ({
  is_visible,
  plans,
  selectedPlanId,
  selectedAddons = [],
  copy,
  footer,
  actions = {}
}: TpOnlyRenewalsProps) => {
  const { isDesktop } = useContext(Context);
  const model = useMemo(
    () => buildTpEditModel(plans, selectedPlanId),
    [plans, selectedPlanId]
  );
  const initialPlanId = model.selectedPlanId;
  const [activePlanId, setActivePlanId] = useState<TpPlanId | undefined>(
    initialPlanId
  );
  const [selectionsByPlan, setSelectionsByPlan] = useState<
    Partial<Record<TpPlanId, AddonSelectionMap>>
  >(() => buildInitialSelections(model.plans, initialPlanId, selectedAddons));
  const [modalAddon, setModalAddon] = useState<TpAddonModel | null>(null);
  const [paModalMode, setPaModalMode] = useState<"selector" | "nudge" | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const visitedPlans = useRef(
    new Set<TpPlanId>(initialPlanId ? [initialPlanId] : [])
  );

  if (!is_visible) return null;

  const activePlan = model.plans.find((plan) => plan.id === activePlanId);
  if (!activePlan || !initialPlanId) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "tp_only_renewals requires a supported TP plan with a valid net premium"
      );
    }
    return null;
  }

  const activeSelections = selectionsByPlan[activePlan.id] || {};
  const planCards = getPlanCards(
    model.plans,
    activePlan.id,
    copy?.planPriceNudgeTemplate
  );
  const benefitCards = getBenefitCards(
    activePlan,
    activeSelections,
    copy?.addonTitleTemplate
  );
  const totalNetPremium = calculateTotalNetPremium(
    activePlan,
    activeSelections,
    model.plans
  );
  const hasValidSubmissionData =
    Number.isFinite(totalNetPremium) &&
    areSelectionsValid(activePlan, activeSelections, model.plans);
  const footerPrice = formatPremium(totalNetPremium);
  const footerConfig = footer
    ? {
        ...footer,
        ...copy?.footer
      }
    : undefined;
  const ctaLabel = isDesktop
    ? footerConfig?.desktopCtaLabel || footerConfig?.mobileCtaLabel
    : footerConfig?.mobileCtaLabel || footerConfig?.desktopCtaLabel;
  const ctaActionKey =
    footerConfig?.actionKey ||
    (isDesktop
      ? footerConfig?.desktopActionKey || footerConfig?.mobileActionKey
      : footerConfig?.mobileActionKey || footerConfig?.desktopActionKey);
  const canRenderPaModal = Boolean(
    copy?.paModal?.badgeLabel?.trim() &&
      copy.paModal.heading?.trim() &&
      copy.paModal.nudgeHeading?.trim() &&
      copy.paModal.description?.trim() &&
      copy.paModal.coverageList?.length &&
      copy.paModal.primaryCtaLabel?.trim() &&
      copy.paModal.nudgePrimaryCtaLabel?.trim() &&
      copy.paModal.nudgeSecondaryCtaLabel?.trim()
  );

  if (!copy?.title?.trim()) warnForMissingCopy("title is missing");
  if (!copy?.planSectionTitle?.trim()) {
    warnForMissingCopy("planSectionTitle is missing");
  }
  if (!copy?.benefitsSectionTitle?.trim()) {
    warnForMissingCopy("benefitsSectionTitle is missing");
  }
  if (!ctaLabel?.trim()) warnForMissingCopy("Continue CTA label is missing");

  const updateActiveSelections = (
    updater: (current: AddonSelectionMap) => AddonSelectionMap
  ) => {
    setSelectionsByPlan((current) => ({
      ...current,
      [activePlan.id]: updater(current[activePlan.id] || {})
    }));
  };

  const handlePlanSelect = (planId: string) => {
    const targetPlan = model.plans.find((plan) => plan.id === planId);
    if (!targetPlan || targetPlan.id === activePlan.id || isSubmitting) return;

    if (!visitedPlans.current.has(targetPlan.id)) {
      setSelectionsByPlan((current) => ({
        ...current,
        [targetPlan.id]: carrySelectionsToPlan(
          targetPlan,
          current[activePlan.id] || {},
          current[targetPlan.id] || {}
        )
      }));
      visitedPlans.current.add(targetPlan.id);
    }

    setModalAddon(null);
    setPaModalMode(null);
    setActivePlanId(targetPlan.id);
  };

  const getSelectionForVariant = (addon: TpAddonModel, variant: AddonVariant) =>
    selectionFromAddon(addon, {
      id: addon.id,
      variant_id: variant.variant_id || undefined,
      tenure: variant.tenure || addon.tenure,
      sum_assured_value: variant.sum_assured_value || undefined
    });

  const submitContinue = (selections: AddonSelectionMap) => {
    const actionConfig = ctaActionKey ? actions[ctaActionKey] || [] : [];
    const totalPremium = calculateTotalNetPremium(
      activePlan,
      selections,
      model.plans
    );
    const canSubmit =
      Number.isFinite(totalPremium) &&
      areSelectionsValid(activePlan, selections, model.plans);

    if (isSubmitting || !canSubmit || actionConfig.length === 0) {
      return;
    }

    setIsSubmitting(true);
    executeActions(actionConfig, {
      data: {
        plan_id: activePlan.id,
        selected_addons: serializeSelections(selections)
      }
    });
  };

  const handleBenefitToggle = (benefit: BenefitCard) => {
    if (isSubmitting) return;
    const addon = activePlan.addons.find((item) => item.id === benefit.id);
    if (!addon) return;

    if (addon.id === PA_OWNER_ADDON_ID && addon.variants.length > 0) {
      if (!canRenderPaModal) {
        warnForMissingCopy(
          "Personal Accident modal copy is missing; modal was not opened"
        );
        return;
      }
      setPaModalMode("selector");
      setModalAddon(addon);
      return;
    }

    if (addon.variants.length > 1) {
      if (
        !copy?.variantModal?.headingTemplate ||
        !copy.variantModal.optionsLabelTemplate ||
        !copy.variantModal.primaryCtaLabel ||
        !copy.variantModal.secondaryCtaLabel
      ) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            "[tp_only_renewals] variant modal copy is missing; modal was not opened"
          );
        }
        return;
      }
      setModalAddon(addon);
      setPaModalMode(null);
      return;
    }

    updateActiveSelections((current) => {
      const updated = { ...current };
      if (updated[addon.id]) {
        delete updated[addon.id];
      } else {
        updated[addon.id] = selectionFromAddon(addon);
      }
      return updated;
    });
  };

  const handleVariantSelect = (variant: AddonVariant) => {
    if (!modalAddon) return;
    updateActiveSelections((current) => ({
      ...current,
      [modalAddon.id]: getSelectionForVariant(modalAddon, variant)
    }));
  };

  const handlePaVariantSelect = (variant: AddonVariant) => {
    if (!modalAddon || modalAddon.id !== PA_OWNER_ADDON_ID) return;

    const updatedSelections = {
      ...activeSelections,
      [modalAddon.id]: getSelectionForVariant(modalAddon, variant)
    };
    setSelectionsByPlan((current) => ({
      ...current,
      [activePlan.id]: updatedSelections
    }));

    if (paModalMode === "nudge") {
      submitContinue(updatedSelections);
    }
  };

  const handleVariantRemove = () => {
    if (!modalAddon) return;
    updateActiveSelections((current) => {
      const updated = { ...current };
      delete updated[modalAddon.id];
      return updated;
    });
  };

  const handleContinue = () => {
    if (isSubmitting || !hasValidSubmissionData) {
      return;
    }

    const paAddon = activePlan.addons.find(
      (addon) => addon.id === PA_OWNER_ADDON_ID
    );
    if (
      paAddon &&
      paAddon.variants.length > 0 &&
      !activeSelections[PA_OWNER_ADDON_ID]
    ) {
      if (canRenderPaModal) {
        setModalAddon(paAddon);
        setPaModalMode("nudge");
        return;
      }
      warnForMissingCopy(
        "Personal Accident modal copy is missing; continuing without the nudge"
      );
    }

    submitContinue(activeSelections);
  };

  const selectedModalSelection = modalAddon
    ? activeSelections[modalAddon.id]
    : undefined;
  const defaultModalVariantId =
    modalAddon?.variantId || modalAddon?.variants[0]?.variant_id || "";
  const modalTitle = modalAddon
    ? interpolateTemplate(copy?.variantModal?.headingTemplate, {
        display_name: modalAddon.displayName
      })
    : "";
  const modalOptionsLabel = modalAddon
    ? interpolateTemplate(copy?.variantModal?.optionsLabelTemplate, {
        variant_count: String(modalAddon.variants.length)
      })
    : "";
  const modalHeading =
    modalTitle && modalOptionsLabel
      ? `${modalTitle}<br/><span style="color:rgba(124, 71, 225, 1)">${modalOptionsLabel}</span>`
      : modalTitle;
  const modalPrimaryCtaLabel =
    copy?.variantModal?.primaryCtaLabel?.trim() || "";
  const modalSecondaryCtaLabel =
    copy?.variantModal?.secondaryCtaLabel?.trim() || "";
  const canRenderVariantModal = Boolean(
    modalHeading && modalPrimaryCtaLabel && modalSecondaryCtaLabel
  );
  const modalBadge =
    modalAddon?.badge?.text && modalAddon.badge.type
      ? {
          text: modalAddon.badge.text,
          type: modalAddon.badge.type
        }
      : undefined;
  const rsaModalBadge =
    modalAddon?.variants.find((variant) => variant.badge?.text)?.badge ||
    modalBadge;

  return (
    <>
      <TpOnlyRenewalsUI
        title={copy?.title}
        planSectionTitle={copy?.planSectionTitle}
        benefitsSectionTitle={copy?.benefitsSectionTitle}
        plans={planCards}
        benefits={benefitCards}
        footer={footerConfig}
        isDesktop={isDesktop}
        isSubmitting={isSubmitting}
        isCtaDisabled={!hasValidSubmissionData}
        ctaLabel={ctaLabel}
        footerPrice={footerPrice}
        onSelectPlan={handlePlanSelect}
        onToggleBenefit={handleBenefitToggle}
        onCtaClick={handleContinue}
      />

      {canRenderPaModal &&
      paModalMode &&
      copy?.paModal &&
      modalAddon?.id === PA_OWNER_ADDON_ID ? (
        <TpPaCoverModal
          addon={modalAddon}
          copy={copy.paModal}
          isCoverSelected={Boolean(selectedModalSelection)}
          mode={paModalMode}
          selectedVariantId={selectedModalSelection?.variant_id}
          onClose={() => {
            setModalAddon(null);
            setPaModalMode(null);
          }}
          onContinueWithout={() => submitContinue(activeSelections)}
          onRemove={handleVariantRemove}
          onSelect={handlePaVariantSelect}
        />
      ) : null}

      {canRenderVariantModal && modalAddon?.id === RSA_ADDON_ID ? (
        <AddonVariantsModal
          showModal
          variants={modalAddon.variants}
          primaryCtaClickHandler={handleVariantSelect}
          secondaryCtaClickHandler={handleVariantRemove}
          isCoverSelected={Boolean(selectedModalSelection)}
          modalContent={{
            badge: rsaModalBadge,
            removeCtaText: modalSecondaryCtaLabel,
            primaryCtaText: modalPrimaryCtaLabel,
            heading: modalHeading
          }}
          handleClose={() => setModalAddon(null)}
          id="tp_pro_rsa_variants_modal"
          selectedVariantId={selectedModalSelection?.variant_id || null}
          defaultVariantIdSelection={defaultModalVariantId}
          addonId={modalAddon.id}
        />
      ) : null}
    </>
  );
};

export default connect(mapStateToProps)(TpOnlyRenewals);
